import store from "@utils/redux/store";
import { baselineRoutes, inAppRoutes, routeConstants } from "@utils/constants";

/**
 * Verifies if current user is from US with EN language
 * @returns {boolean|boolean}
 */
export const isEnUS = () => {
  const { geoLocation, language } = store.getState();
  return geoLocation.country_code === "US" && language === "en";
};

/**
 * Verifies if current user is on main routes and is from US with EN language
 * @returns {boolean|boolean}
 */
export const isEnUSBaseline = () => {
  // DO NOT EDIT BASELINE ROUTES!
  const BASELINE_ROUTES = [
    routeConstants.direct,
    routeConstants.exsf01,
    routeConstants.exsf01d,
    routeConstants.affiliateNoTrial,
    routeConstants.affiliateNoTrialPromo,
  ];
  const { routeId } = store.getState();
  return isEnUS() && BASELINE_ROUTES.includes(routeId);
};

/**
 * Verifies if the user is in baseline routes and language_code en
 * It also gives the possibility to add extra verification rule in addition to ones above
 */
export const isEnBaselineAndExtraRule = (extraVerification) => {
  const { routeId, language } = store.getState();
  // DO NOT EDIT BASELINE ROUTES!
  const BASELINE_ROUTES = [
    routeConstants.direct,
    routeConstants.exsf01,
    routeConstants.exsf01d,
  ];
  return (
    language === "en" && BASELINE_ROUTES.includes(routeId) && extraVerification
  );
};

/**
 * Verifies if current user is on winback route and is from US with EN language
 * @returns {boolean|boolean}
 */
export const isEnUSWinback = () => {
  const { routeId } = store.getState();
  return isEnUS() && routeId === routeConstants.winback;
};

/**
 * Verifies if current user is eligible for HM program
 * @returns {boolean|boolean}
 */
export const isHMEligible = () => {
  const { routeId, deviceInformation } = store.getState();
  const ROUTES = [
    routeConstants.direct,
    routeConstants.exsf01,
    routeConstants.exsf01d,
  ];
  return (
    isEnUS() &&
    ROUTES.includes(routeId) &&
    deviceInformation.device_is_mobile &&
    deviceInformation.device_os === "iOS"
  );
};

/**
 * Verifies if current user is on email-winback route and is from US with EN language
 * @returns {boolean|boolean}
 */
export const isEnUSEmailWinback = () => {
  const { routeId } = store.getState();
  return isEnUS() && routeId === routeConstants.emailWinback;
};

/**
 * Verifies if current user is on winback core routes(/winback/ and /email-winback/) and is from US with EN language
 * @returns {boolean|boolean}
 */
export const isEnUSCoreWinback = () => isEnUSWinback() || isEnUSEmailWinback();

/**
 * Verifies if current user is on (winback or baseline/main routes) and is from US with EN language
 * @returns {boolean|boolean}
 */
export const isEnUSBaselineOrWinback = () =>
  isEnUSBaseline() || isEnUSWinback();

/**
 * Verifies if current user is on (core-winback or baseline/main routes) and is from US with EN language
 * @returns {boolean|boolean}
 */
export const isEnUSBaselineOrCoreWinback = () =>
  isEnUSBaseline() || isEnUSCoreWinback();

/**
 * Verifies if current user is on inapp winback route and is from US with EN language
 * @returns {boolean|boolean}
 */
export const isEnUSInAppWinback = () => {
  const { routeId } = store.getState();
  return (
    (routeId === routeConstants.app14DayFtWeb ||
      routeId === routeConstants.app14DayReturning) &&
    isEnUS()
  );
};

/**
 * Verifies if current user is on inapp winback route and has EN language
 * @returns {boolean|boolean}
 */
export const isEnInAppWinback = () => {
  const { routeId, language } = store.getState();
  return (
    (routeId === routeConstants.app14DayFtWeb ||
      routeId === routeConstants.app14DayReturning) &&
    language === "en"
  );
};

/**
 * Verifies if current user is in an in-app route
 * @returns {boolean|boolean}
 */
export const isInApp = () => {
  const { serverContext } = store.getState();
  return serverContext.use_in_app_survey;
};

/**
 * Verifies if current user is in an email-route
 * @returns {boolean|boolean}
 */
export const isEmailRoute = () => {
  const { routeId } = store.getState();
  return (
    routeId === routeConstants.emailMain ||
    routeId === routeConstants.emailWinback
  );
};

/**
 * Verifies if user is eligible for collapsed payment enrollment form
 * @returns {boolean|boolean}
 */
export const isEligibleForCollapsedPayment = () => {
  const { geoLocation, language, routeId } = store.getState();
  return (
    language === "en" &&
    (geoLocation.country_code === "US" || baselineRoutes.includes(routeId)) &&
    !isInApp()
  );
};

/**
 * Verifies if current user is on main routes and with EN language
 * @returns {boolean|boolean}
 */
export const isEnBaseline = () => {
  const { language, routeId } = store.getState();
  return language === "en" && baselineRoutes.includes(routeId);
};

/**
 * Verifies if current user is on in-app routes
 * @returns {boolean}
 */
export const isApp14DayBaseRoute = () => {
  const { serverContext, routeId } = store.getState();
  return (
    serverContext?.use_in_app_survey &&
    [routeConstants.app14DayWeb, routeConstants.app14Day].includes(routeId)
  );
};

/**
 * Verifies that the user is on the English US buyflow on the web (not inapp)
 */
export const isEnUSWeb = () => {
  return isEnUS() && !isInApp();
};
